import type { BoxProps } from '@hover/blueprint';
import { Body, Box, Button, Heading, transition } from '@hover/blueprint';
import { SimpleGrid, SlideFade, useMediaQuery } from '@hover/blueprint/chakra';
import React from 'react';

import { MenuHeader } from 'src/modules/designStudio/components/DesignMenu/Menu/MenuHeader';
import { PaletteRow } from 'src/modules/designStudio/components/DesignMenu/Menu/TemplateMenu/PalleteRow';
import { TemplateCard } from 'src/modules/designStudio/components/DesignMenu/Menu/TemplateMenu/TemplateCard';
import type { TemplatePaletteConfig } from 'src/modules/designStudio/helpers/data/PaletteStyles';
import type { TemplateWithIcon } from 'src/modules/designStudio/helpers/data/TemplateStyles';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';

const ShowProductsInTemplateButton = ({
  showProductsButton,
  onViewProducts,
}: {
  showProductsButton: boolean;
  onViewProducts: () => void;
}) => {
  const t = useTranslate(TranslationNamespace.DESIGN_STUDIO);
  return (
    <Box
      alignItems="stretch"
      flexDirection="row"
      justifyItems="stretch"
      maxHeight={showProductsButton ? '200px' : '0'}
      width="100%"
      {...transition.easing250('max-height')}
    >
      <SlideFade
        in={showProductsButton}
        offsetY="100%"
        style={{ flexGrow: 1 }}
        unmountOnExit
      >
        <Button
          flexGrow={1}
          my={200}
          onClick={onViewProducts}
          py={500}
          shape="box"
          width="100%"
        >
          {t('menu.template.viewProductsInPalette')}
        </Button>
      </SlideFade>
    </Box>
  );
};

export interface TemplatesViewProps extends BoxProps {
  closeMenu: () => void;
  templates: TemplateWithIcon[];
  palettes: TemplatePaletteConfig[];
  selectedTemplateIndex: number | undefined;
  selectedPaletteIndex: number;
  onTemplateSelected: (index: number) => void;
  onPaletteSelected: (index: number) => void;
  onPaletteRowShowMore: () => void;
  onPaletteRowCancel: () => void;
  isMiniPaletteRowVisible: boolean;
  doShowViewProductsButton: boolean;
  onViewProducts: () => void;
}

export const TemplatesView = ({
  closeMenu,
  templates,
  palettes,
  selectedTemplateIndex,
  selectedPaletteIndex,
  onTemplateSelected,
  onPaletteSelected,
  onPaletteRowCancel,
  onPaletteRowShowMore,
  isMiniPaletteRowVisible,
  doShowViewProductsButton,
  onViewProducts,
  ...props
}: TemplatesViewProps) => {
  const t = useTranslate(TranslationNamespace.DESIGN_STUDIO);

  const showProductsButton = selectedTemplateIndex !== undefined;

  const [isMobileOrTablet] = useMediaQuery('(max-width: 834px)');

  return (
    <Box
      alignItems="stretch"
      bottom="0"
      flexDirection="column"
      height="100%"
      left="0"
      overflowX="hidden"
      padding="600"
      position="absolute"
      right="0"
      top="0"
      {...props}
    >
      <MenuHeader
        closeMenu={closeMenu}
        headerText={t('menu.template.title')!}
      />
      <Body alignSelf="center" fontWeight={100} mb={400} mt={0} size={400}>
        {t('menu.template.subtitle')}
      </Body>
      <Heading as="h2" marginBottom={300} size={300}>
        {t('menu.template.listHeader')}
      </Heading>
      <SimpleGrid
        columns={{ base: 2, tablet: undefined }}
        display={{ base: 'flex', tablet: 'grid' }}
        flexShrink={1}
        gap={400}
        margin="-100"
        minChildWidth="120px"
        minHeight="80px"
        overflowX={{ base: 'auto', tablet: 'visible' }}
        overflowY={{ base: 'visible', tablet: 'auto' }}
        padding="100"
      >
        {templates.map((template, index) => (
          <TemplateCard
            alignSelf="center"
            height={{ base: '100%', tablet: 'auto' }}
            imgPath={template.img}
            isSelected={selectedTemplateIndex === index}
            justifySelf="center"
            key={template.img}
            maxWidth="160px"
            onSelected={() => {
              onTemplateSelected(index);
            }}
            template={template.style}
            width="100%"
          />
        ))}
      </SimpleGrid>
      {doShowViewProductsButton && isMobileOrTablet && (
        <ShowProductsInTemplateButton
          onViewProducts={onViewProducts}
          showProductsButton={showProductsButton}
        />
      )}
      <Box
        alignItems="stretch"
        flexDirection="column"
        justifyContent="stretch"
        maxHeight={isMiniPaletteRowVisible ? '200px' : '0'}
        {...transition.easing250('max-height')}
      >
        <SlideFade in={isMiniPaletteRowVisible} offsetY="100%" unmountOnExit>
          <PaletteRow
            onPaletteRowCancel={onPaletteRowCancel}
            onPaletteRowShowMore={onPaletteRowShowMore}
            onPaletteSelected={onPaletteSelected}
            palettes={palettes}
            selectedPaletteIndex={selectedPaletteIndex}
            width="100%"
          />
        </SlideFade>
      </Box>
      {doShowViewProductsButton && !isMobileOrTablet && (
        <ShowProductsInTemplateButton
          onViewProducts={onViewProducts}
          showProductsButton={showProductsButton}
        />
      )}
    </Box>
  );
};
